import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import ExcelJS from 'exceljs';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        container: {
            backgroundColor: colors.bg,
            minHeight: '100vh',
            padding: '20px'
        },
        header: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 10px 0'
        },
        subtitle: {
            fontSize: '16px',
            color: colors.subtleText,
            margin: '0'
        },
        navLink: {
            color: colors.link,
            textDecoration: 'none',
            fontSize: '16px',
            marginBottom: '20px',
            display: 'inline-block'
        },
        reportSection: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 15px 0',
            borderBottom: `2px solid ${colors.border}`,
            paddingBottom: '10px'
        },
        chartContainer: {
            position: 'relative',
            height: '400px',
            margin: '20px 0'
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px'
        },
        noDataContainer: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic'
        },
        controlsContainer: {
            display: 'flex',
            gap: '15px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            alignItems: 'center'
        },
        filterSelect: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px'
        },
        refreshButton: {
            padding: '8px 16px',
            backgroundColor: colors.link,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
        }
    };
};

function ReportsPage({ theme }) {
    const styles = getStyles(theme);
    const [dailyACTimeData, setDailyACTimeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [siteFilter, setSiteFilter] = useState('');
    const [sites, setSites] = useState([]);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [exportLoading, setExportLoading] = useState(false);

    // Fetch sites for filtering
    useEffect(() => {
        api.get('/sites/')
            .then(res => {
                setSites(res.data.results || res.data || []);
            })
            .catch(err => {
                console.error('Error fetching sites:', err);
            });
    }, []);

    // Fetch Daily AC Time data
    const fetchDailyACTimeData = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (siteFilter) params.append('site_name', siteFilter);
        
        api.get(`/daily-ac-time/?${params.toString()}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setDailyACTimeData(data);
            })
            .catch(err => {
                console.error('Error fetching daily AC time data:', err);
                setAlert({ show: true, message: 'Failed to fetch Daily AC Time data.', type: 'error' });
            })
            .finally(() => setLoading(false));
    }, [siteFilter]);

    useEffect(() => {
        fetchDailyACTimeData();
    }, [fetchDailyACTimeData]);

    // Process data for BUNO vs Hours chart
    const processBUNOHoursData = () => {
        if (!dailyACTimeData.length) return null;

        // Create a color mapping for different phases
        const phaseColors = {};
        const availableColors = [
            'rgba(4, 106, 56, 0.8)',     // Amentum Green
            'rgba(83, 87, 90, 0.8)'      // Amentum Gray
        ];

        const borderColors = [
            'rgba(4, 106, 56, 1)',       // Amentum Green
            'rgba(83, 87, 90, 1)'        // Amentum Gray
        ];

        // Process data to get BUNOs and their hours
        const bunoData = dailyACTimeData.map(item => {
            const buno = item.t_buno || 'Unknown';
            const hours = parseFloat(item.t_hours_to_phase) || 0;
            const phase = item.t_next_phase || 'Unknown';
            
            // Assign color to phase if not already assigned
            if (!phaseColors[phase]) {
                const colorIndex = Object.keys(phaseColors).length % availableColors.length;
                phaseColors[phase] = {
                    backgroundColor: availableColors[colorIndex],
                    borderColor: borderColors[colorIndex]
                };
            }
            
            return {
                buno,
                hours,
                phase,
                modex: item.t_modex
            };
        });

        // Sort by hours (ascending) to show highest hours first
        bunoData.sort((a, b) => a.hours - b.hours);

        return {
            labels: bunoData.map(item => item.buno),
            datasets: [{
                label: 'Hours Remaining',
                data: bunoData.map(item => item.hours),
                backgroundColor: bunoData.map(item => phaseColors[item.phase].backgroundColor),
                borderColor: bunoData.map(item => phaseColors[item.phase].borderColor),
                borderWidth: 2
            }],
            bunoDetails: bunoData,
            phaseColors: phaseColors
        };
    };

    const bunoHoursData = processBUNOHoursData();

    // Helper function to generate chart as image buffer
    const generateChartImage = async (bunoData, siteName) => {
        // Create a temporary canvas element
        const canvas = document.createElement('canvas');
        canvas.width = 1000;
        canvas.height = 500;
        canvas.style.position = 'absolute';
        canvas.style.left = '-9999px';
        document.body.appendChild(canvas);
        
        try {
            const ctx = canvas.getContext('2d');
            
            // Create phase color mapping (same as in processBUNOHoursData)
            const phaseColors = {};
            const availableColors = [
                'rgba(4, 106, 56, 0.8)',     // Amentum Green
                'rgba(83, 87, 90, 0.8)'      // Amentum Gray
            ];
            
            const borderColors = [
                'rgba(4, 106, 56, 1)',       // Amentum Green
                'rgba(83, 87, 90, 1)'        // Amentum Gray
            ];
            
            // Assign colors to phases
            bunoData.forEach(item => {
                if (!phaseColors[item.phase]) {
                    const colorIndex = Object.keys(phaseColors).length % availableColors.length;
                    phaseColors[item.phase] = {
                        backgroundColor: availableColors[colorIndex],
                        borderColor: borderColors[colorIndex]
                    };
                }
            });
            
            // Create chart data
            const chartData = {
                labels: bunoData.map(item => item.buno),
                datasets: [{
                    label: 'Hours Remaining',
                    data: bunoData.map(item => item.hours),
                    backgroundColor: bunoData.map(item => phaseColors[item.phase].backgroundColor),
                    borderColor: bunoData.map(item => phaseColors[item.phase].borderColor),
                    borderWidth: 2
                }]
            };
            
            // Create chart options
            const chartOptions = {
                type: 'bar',
                data: chartData,
                options: {
                    responsive: false,
                    maintainAspectRatio: false,
                    animation: false,
                    plugins: {
                        legend: {
                            display: false
                        },
                        title: {
                            display: true,
                            text: `BUNO Hours Remaining - ${siteName}`,
                            font: {
                                size: 16,
                                weight: 'bold'
                            }
                        }
                    },
                    scales: {
                        x: {
                            title: {
                                display: true,
                                text: 'BUNO',
                                font: {
                                    size: 12,
                                    weight: 'bold'
                                }
                            },
                            ticks: {
                                maxRotation: 45,
                                minRotation: 45,
                                font: {
                                    size: 10
                                }
                            },
                            grid: {
                                color: 'rgba(0, 0, 0, 0.1)'
                            }
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'Hours Remaining',
                                font: {
                                    size: 12,
                                    weight: 'bold'
                                }
                            },
                            grid: {
                                color: 'rgba(0, 0, 0, 0.1)'
                            }
                        }
                    }
                }
            };
            
            // Create and render chart
            const chart = new ChartJS(ctx, chartOptions);
            
            // Wait for chart to render
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Create a white background canvas and composite the chart on top
            const whiteCanvas = document.createElement('canvas');
            whiteCanvas.width = canvas.width;
            whiteCanvas.height = canvas.height;
            const whiteCtx = whiteCanvas.getContext('2d');
            
            // Fill white canvas with white background
            whiteCtx.fillStyle = 'white';
            whiteCtx.fillRect(0, 0, whiteCanvas.width, whiteCanvas.height);
            
            // Draw the chart canvas on top of the white background
            whiteCtx.drawImage(canvas, 0, 0);
            
            // Use the white canvas for the final image
            const finalCanvas = whiteCanvas;
            
            // Convert canvas to blob, then to array buffer
            return new Promise((resolve, reject) => {
                finalCanvas.toBlob((blob) => {
                    if (blob) {
                        blob.arrayBuffer().then(buffer => {
                            // Convert ArrayBuffer to Uint8Array for ExcelJS
                            resolve(new Uint8Array(buffer));
                        }).catch(reject);
                    } else {
                        reject(new Error('Failed to create chart image'));
                    }
                }, 'image/png');
            }).finally(() => {
                // Clean up
                chart.destroy();
                document.body.removeChild(canvas);
            });
        } catch (error) {
            document.body.removeChild(canvas);
            throw error;
        }
    };

    const handleExportWaterfallChart = async () => {
        try {
            setExportLoading(true);
            
            // Fetch all daily AC time data (without site filter)
            const dailyACResponse = await api.get('/daily-ac-time/');
            const allData = Array.isArray(dailyACResponse.data) ? dailyACResponse.data : (dailyACResponse.data.results || []);
            
            // Fetch inspections with status "Not Started" or "In Progress"
            const inspectionsResponse = await api.get('/inspections/');
            const allInspections = Array.isArray(inspectionsResponse.data) 
                ? inspectionsResponse.data 
                : (inspectionsResponse.data.results || []);
            
            // Filter inspections to only "Not Started" and "In Progress"
            const filteredInspections = allInspections.filter(insp => 
                insp.status === 'Not Started' || insp.status === 'In Progress'
            );
            
            // Group data by site
            const sites = [...new Set(allData.map((item) => item.site_name).filter(Boolean))].sort();
            const dataBySite = {};
            const inspectionsBySite = {};
            
            sites.forEach(site => {
                dataBySite[site] = allData.filter(item => item.site_name === site);
                // Group inspections by site name (site field is a string, not object)
                inspectionsBySite[site] = filteredInspections.filter(insp => {
                    // Handle both string and object site formats
                    const siteName = typeof insp.site === 'string' ? insp.site : insp.site?.name || '';
                    return siteName === site;
                });
            });
            
            // Create Excel workbook
            const workbook = new ExcelJS.Workbook();
            
            // Process each site
            for (const site of sites) {
                const siteData = dataBySite[site];
                if (!siteData || siteData.length === 0) continue;
                
                // Process data for this site
                const bunoData = siteData.map(item => ({
                    buno: item.t_buno || 'Unknown',
                    hours: parseFloat(item.t_hours_to_phase) || 0,
                    phase: item.t_next_phase || 'Unknown'
                }));
                
                // Sort by hours (ascending)
                bunoData.sort((a, b) => a.hours - b.hours);
                
                // Create worksheet for this site
                const worksheet = workbook.addWorksheet(site);
                
                // Add title
                const titleRow = worksheet.addRow([`Waterfall Chart - ${site}`]);
                titleRow.font = { size: 16, bold: true };
                worksheet.mergeCells(1, 1, 1, 3);
                titleRow.getCell(1).alignment = { horizontal: 'center' };
                
                // Add empty row
                worksheet.addRow([]);
                
                // Waterfall chart data (BUNO and Hours Remaining)
                const chartHeaderRow = worksheet.addRow(['BUNO', 'Next Phase Inspection', 'Hours Remaining']);
                chartHeaderRow.font = { bold: true, size: 12 };
                chartHeaderRow.getCell(1).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                chartHeaderRow.getCell(2).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                chartHeaderRow.getCell(3).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                chartHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
                
                // Add chart data rows
                bunoData.forEach(item => {
                    const row = worksheet.addRow([item.buno, item.phase, item.hours]);
                    row.getCell(2).numFmt = '0.0'; // Format hours with 1 decimal
                });
                
                // Generate and add bar chart image
                try {
                    const chartImageBuffer = await generateChartImage(bunoData, site);
                    const imageId = workbook.addImage({
                        buffer: chartImageBuffer,
                        extension: 'png',
                    });
                    
                    // Add image after the chart data table (starting at column D, row after data)
                    const chartImageRow = 3 + bunoData.length + 1; // After header + data rows
                    worksheet.addImage(imageId, {
                        tl: { col: 3.5, row: chartImageRow },
                        ext: { width: 800, height: 400 }
                    });
                } catch (error) {
                    console.error(`Error generating chart for ${site}:`, error);
                    // Continue without chart if generation fails
                }
                
                // Add empty rows before ±10% Aircraft section
                worksheet.addRow([]);
                worksheet.addRow([]);
                
                // ±10% Aircraft section header (aircraft within ±40 hours of phase inspection)
                const plusMinus10HeaderRow = worksheet.addRow(['±10% Aircraft']);
                plusMinus10HeaderRow.font = { size: 14, bold: true };
                worksheet.mergeCells(worksheet.rowCount, 1, worksheet.rowCount, 3);
                plusMinus10HeaderRow.getCell(1).alignment = { horizontal: 'center' };
                
                // Filter aircraft within ±40 hours (±10% window)
                const plusMinus10Aircraft = bunoData.filter(item => item.hours >= -40 && item.hours <= 40);
                
                // ±10% Aircraft table headers
                const plusMinus10TableHeader = worksheet.addRow(['BUNO', 'Next Phase Inspection', 'Hours Remaining']);
                plusMinus10TableHeader.font = { bold: true, size: 11 };
                plusMinus10TableHeader.getCell(1).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                plusMinus10TableHeader.getCell(2).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                plusMinus10TableHeader.getCell(3).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                plusMinus10TableHeader.alignment = { horizontal: 'center', vertical: 'middle' };
                
                // Add ±10% Aircraft data rows (each BUNO in a single row)
                if (plusMinus10Aircraft.length > 0) {
                    plusMinus10Aircraft.forEach(item => {
                        const row = worksheet.addRow([item.buno, item.phase, item.hours]);
                        row.getCell(3).numFmt = '0.0'; // Format hours with 1 decimal
                    });
                } else {
                    // Add a row indicating no aircraft in this range
                    const noDataRow = worksheet.addRow(['No aircraft in ±10% window', '', '']);
                    noDataRow.getCell(1).font = { italic: true };
                    worksheet.mergeCells(worksheet.rowCount, 1, worksheet.rowCount, 3);
                }
                
                // Set column widths (will be expanded for inspection section if needed)
                worksheet.columns = [
                    { width: 15 }, // BUNO
                    { width: 25 }, // Hours Remaining / Next Phase Inspection
                    { width: 15 }, // Hours Remaining (in detail table)
                    { width: 0 },  // Column D (for chart image)
                    { width: 0 },  // Column E (for inspection data)
                    { width: 0 },  // Column F (for inspection data)
                    { width: 0 },  // Column G (for inspection data)
                    { width: 0 }   // Column H (for inspection data)
                ];
                
                // Calculate row numbers for banding
                // Structure: Row 1=Title, Row 2=Empty, Row 3=Chart Header, Rows 4+ = Chart Data
                // Then: Empty rows, ±10% Header, ±10% Table Header, ±10% Data
                const chartDataStartRow = 4; // Chart data starts at row 4 (after title, empty, header)
                const plusMinus10DataStartRow = chartDataStartRow + bunoData.length + 4; // After chart data + 2 empty + header + table header
                
                // Apply borders to all cells
                worksheet.eachRow((row, rowNumber) => {
                    row.eachCell((cell) => {
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                    });
                });
                
                // Apply color banding to data rows
                bunoData.forEach((item, index) => {
                    const chartRowNum = chartDataStartRow + index; // Chart data rows
                    
                    if (index % 2 === 0) {
                        const bandingColor = 'FFF2F2F2';
                        // Apply to chart data row
                        const chartRow = worksheet.getRow(chartRowNum);
                        if (chartRow) {
                            chartRow.eachCell((cell) => {
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: bandingColor }
                                };
                            });
                        }
                    }
                });
                
                // Apply color banding to ±10% aircraft rows
                plusMinus10Aircraft.forEach((item, index) => {
                    const plusMinus10RowNum = plusMinus10DataStartRow + index;
                    
                    if (index % 2 === 0) {
                        const bandingColor = 'FFF2F2F2';
                        const row = worksheet.getRow(plusMinus10RowNum);
                        if (row) {
                            row.eachCell((cell) => {
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: bandingColor }
                                };
                            });
                        }
                    }
                });
                
                // Add Inspections section
                const siteInspections = inspectionsBySite[site] || [];
                if (siteInspections.length > 0) {
                    // Add empty rows before Inspections section
                    worksheet.addRow([]);
                    worksheet.addRow([]);
                    
                    // Inspections section header
                    const inspectionsHeaderRow = worksheet.addRow(['Inspections (Not Started & In Progress)']);
                    inspectionsHeaderRow.font = { size: 14, bold: true };
                    worksheet.mergeCells(worksheet.rowCount, 1, worksheet.rowCount, 5);
                    inspectionsHeaderRow.getCell(1).alignment = { horizontal: 'center' };
                    
                    // Inspections table headers
                    const inspectionsTableHeader = worksheet.addRow([
                        'BUNO', 
                        'Inspection', 
                        'Status', 
                        'ECD', 
                        'Progress %'
                    ]);
                    inspectionsTableHeader.font = { bold: true, size: 11 };
                    inspectionsTableHeader.getCell(1).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                    inspectionsTableHeader.getCell(2).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                    inspectionsTableHeader.getCell(3).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                    inspectionsTableHeader.getCell(4).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                    inspectionsTableHeader.getCell(5).fill = {type: 'pattern',pattern: 'solid', fgColor: { argb: 'FFD3D3D3' }};
                    inspectionsTableHeader.alignment = { horizontal: 'center', vertical: 'middle' };
                    
                    // Update column widths for inspection section
                    worksheet.getColumn(1).width = 15; // BUNO
                    worksheet.getColumn(2).width = 25; // Inspection
                    worksheet.getColumn(3).width = 15; // Status
                    worksheet.getColumn(4).width = 15; // ECD
                    worksheet.getColumn(5).width = 12; // Progress %
                    
                    // Add inspection data rows
                    siteInspections.forEach(insp => {
                        const ecdDate = insp.ecd ? new Date(insp.ecd).toLocaleDateString() : 'N/A';
                        const progress = insp.percent_complete !== undefined ? insp.percent_complete : 0;
                        
                        const row = worksheet.addRow([
                            insp.buno || 'N/A',
                            insp.checklist_type || 'N/A',
                            insp.status || 'N/A',
                            ecdDate,
                            progress
                        ]);
                        
                        // Format progress as percentage
                        row.getCell(5).numFmt = '0.0';
                    });
                    
                    // Apply borders to inspection rows
                    const inspectionsStartRow = worksheet.rowCount - siteInspections.length;
                    for (let i = inspectionsStartRow - 1; i <= worksheet.rowCount; i++) {
                        const row = worksheet.getRow(i);
                        if (row) {
                            row.eachCell((cell) => {
                                cell.border = {
                                    top: { style: 'thin' },
                                    left: { style: 'thin' },
                                    bottom: { style: 'thin' },
                                    right: { style: 'thin' }
                                };
                            });
                        }
                    }
                    
                    // Apply color banding to inspection rows
                    siteInspections.forEach((insp, index) => {
                        const inspectionRowNum = inspectionsStartRow + 1 + index; // +1 for header row
                        
                        if (index % 2 === 0) {
                            const bandingColor = 'FFF2F2F2';
                            const row = worksheet.getRow(inspectionRowNum);
                            if (row) {
                                row.eachCell((cell) => {
                                    // Only apply if cell doesn't already have a fill (from borders)
                                    if (!cell.fill || !cell.fill.fgColor) {
                                        cell.fill = {
                                            type: 'pattern',
                                            pattern: 'solid',
                                            fgColor: { argb: bandingColor }
                                        };
                                    }
                                });
                            }
                        }
                    });
                }
            }
            
            // Generate Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Waterfall_Chart_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Error exporting waterfall chart:', error);
            setAlert({ show: true, message: 'Failed to export waterfall chart to Excel.', type: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: 'BUNO Hours Remaining by Next Phase',
                font: {
                    size: 16,
                    weight: 'bold'
                },
                color: theme === 'dark' ? '#e0e0e0' : '#333'
            },
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    afterLabel: function(context) {
                        if (bunoHoursData && bunoHoursData.bunoDetails[context.dataIndex]) {
                            const bunoDetail = bunoHoursData.bunoDetails[context.dataIndex];
                            return [
                                `Next Phase: ${bunoDetail.phase}`,
                                `Modex: ${bunoDetail.modex || 'N/A'}`
                            ];
                        }
                        return '';
                    }
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'BUNO',
                    color: theme === 'dark' ? '#e0e0e0' : '#333'
                },
                ticks: {
                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                    maxRotation: 45,
                    minRotation: 45
                },
                grid: {
                    color: theme === 'dark' ? '#444' : '#ddd'
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Hours Remaining',
                    color: theme === 'dark' ? '#e0e0e0' : '#333'
                },
                ticks: {
                    color: theme === 'dark' ? '#e0e0e0' : '#333'
                },
                grid: {
                    color: theme === 'dark' ? '#444' : '#ddd'
                }
            }
        }
    };

    return (
        <div style={styles.container}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
            
            <div style={styles.header}>
                <Link to="/reports" style={styles.navLink}>&larr; Back to Reports</Link>
                <h1 style={styles.title}>Phase Waterfall</h1>
                <p style={styles.subtitle}>Hours remaining until next phase inspection</p>
            </div>

            <div style={styles.reportSection}>
                <h2 style={styles.sectionTitle}>BUNO Hours Remaining Analysis</h2>
                
                <div style={styles.controlsContainer}>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All Sites</option>
                        {sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                    <button onClick={fetchDailyACTimeData} style={styles.refreshButton}>
                        Refresh Data
                    </button>
                    <button 
                        onClick={handleExportWaterfallChart} 
                        disabled={exportLoading}
                        style={{...styles.refreshButton, backgroundColor: '#28a745'}}
                    >
                        {exportLoading ? 'Exporting...' : 'Export to Excel'}
                    </button>
                </div>

                {loading ? (
                    <div style={styles.loadingContainer}>
                        <Spinner />
                    </div>
                ) : !bunoHoursData ? (
                    <div style={styles.noDataContainer}>
                        No Daily AC Time data available. Please upload data to generate reports.
                    </div>
                ) : (
                    <div style={styles.chartContainer}>
                        <Bar data={bunoHoursData} options={chartOptions} />
                    </div>
                )}

                {bunoHoursData && (
                    <div style={{ marginTop: '20px' }}>
                        <h3 style={{ color: theme === 'dark' ? '#e0e0e0' : '#333', marginBottom: '15px' }}>
                            Phase Color Legend
                        </h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                            {Object.entries(bunoHoursData.phaseColors).map(([phase, colors]) => (
                                <div key={phase} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                    borderRadius: '4px',
                                    border: `1px solid ${theme === 'dark' ? '#444' : '#ddd'}`
                                }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: colors.backgroundColor,
                                        border: `2px solid ${colors.borderColor}`,
                                        borderRadius: '2px'
                                    }}></div>
                                    <span style={{ 
                                        fontSize: '14px', 
                                        color: theme === 'dark' ? '#e0e0e0' : '#333',
                                        fontWeight: '500'
                                    }}>
                                        {phase}
                                    </span>
                                </div>
                            ))}
                        </div>
                        
                        <h3 style={{ color: theme === 'dark' ? '#e0e0e0' : '#333', marginBottom: '15px' }}>
                            BUNO Details
                        </h3>
                        <div style={{ display: 'grid', gap: '10px' }}>
                            {bunoHoursData.bunoDetails
                                .filter(bunoDetail => bunoDetail.hours <= 40)
                                .map((bunoDetail, index) => (
                                <div key={index} style={{
                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                    padding: '15px',
                                    borderRadius: '4px',
                                    border: `1px solid ${theme === 'dark' ? '#444' : '#ddd'}`
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <strong style={{ color: theme === 'dark' ? '#e0e0e0' : '#333' }}>
                                            BUNO: {bunoDetail.buno}
                                        </strong>
                                        <span style={{ 
                                            color: theme === 'dark' ? '#6cb2f7' : '#007bff',
                                            fontWeight: 'bold'
                                        }}>
                                            {bunoDetail.hours.toFixed(1)} hours
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '14px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                                        Next Phase: {bunoDetail.phase} | 
                                        Modex: {bunoDetail.modex || 'N/A'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReportsPage;
